/**
 * One theme vocabulary for the whole platform (docs/15-MUSIC-AND-AI.md,
 * "What churches sing for which themes"). A sermon's themes are read into
 * it, so counts across churches add up: one church's "Easter" and another's
 * "Risen" are both `resurrection`. Each theme has a stable slug (stored,
 * counted, never renamed), a label (what people read) and the words people
 * also use for it.
 *
 * Songs keep a church's own themes as they are; only vocabulary themes count
 * in the platform's statistics.
 */

export const THEME_GROUPS = [
  'church-year',
  'god',
  'christ',
  'spirit',
  'salvation',
  'life',
  'church',
  'sacraments',
  'occasions',
] as const
export type ThemeGroup = (typeof THEME_GROUPS)[number]

export interface ThemeEntry {
  /** Stable: stored and counted. Never renamed; a theme that goes is left out of the list. */
  slug: string
  /** What people read: Title Case. */
  label: string
  /** Other words for it: "Easter" for Resurrection. */
  synonyms: readonly string[]
  group: ThemeGroup
}

const theme = (
  group: ThemeGroup,
  slug: string,
  label: string,
  ...synonyms: string[]
): ThemeEntry => ({ slug, label, synonyms, group })

export const THEMES: readonly ThemeEntry[] = [
  // ---- the church year and its seasons
  theme('church-year', 'advent', 'Advent', 'Advent season', 'Prepare the way', 'O come'),
  theme(
    'church-year',
    'incarnation',
    'Incarnation',
    'Christmas',
    'Nativity',
    'Word made flesh',
    'The Word became flesh',
    'Emmanuel',
    'Immanuel',
    'God with us',
    'Birth of Jesus',
    'Birth of Christ',
  ),
  theme('church-year', 'epiphany', 'Epiphany', 'Magi', 'Wise men', 'Baptism of the Lord'),
  theme('church-year', 'lent', 'Lent', 'Ash Wednesday', 'Lenten season', 'Wilderness'),
  theme('church-year', 'palm-sunday', 'Palm Sunday', 'Triumphal entry', 'Hosanna'),
  theme(
    'church-year',
    'the-cross',
    'The Cross',
    'Cross',
    'Crucifixion',
    'Calvary',
    'Good Friday',
    'The blood',
    'Blood of Jesus',
    'Blood of Christ',
    'Passion',
    'Holy Week',
  ),
  theme(
    'church-year',
    'resurrection',
    'Resurrection',
    'Easter',
    'Risen',
    'He is risen',
    'Risen Christ',
    'Risen Lord',
    'Empty tomb',
    'Easter Sunday',
  ),
  theme('church-year', 'ascension', 'Ascension', 'Ascension Day', 'Christ ascended'),
  theme('church-year', 'pentecost', 'Pentecost', 'Pentecost Sunday', 'Day of Pentecost'),
  theme('church-year', 'trinity', 'Trinity', 'Trinity Sunday', 'Triune God', 'Three in one'),
  theme(
    'church-year',
    'christ-the-king',
    'Christ the King',
    'King of kings',
    'Reign of Christ',
    'Lordship of Christ',
    'Jesus is Lord',
    'Kingship of Christ',
  ),
  theme(
    'church-year',
    'second-coming',
    'Second Coming',
    'Return of Christ',
    'Christs return',
    'Maranatha',
    'Day of the Lord',
    'Coming again',
    'He will come again',
  ),
  theme('church-year', 'all-saints', 'All Saints', 'Communion of saints', 'Cloud of witnesses'),
  theme(
    'church-year',
    'thanksgiving',
    'Thanksgiving',
    'Gratitude',
    'Thankfulness',
    'Giving thanks',
  ),
  theme('church-year', 'harvest', 'Harvest', 'Harvest festival', 'Seedtime and harvest'),
  theme('church-year', 'new-year', 'New Year', 'New beginnings', 'A new start'),

  // ---- God: who he is
  theme(
    'god',
    'holiness',
    'Holiness',
    'Holy',
    'Holiness of God',
    'Gods holiness',
    'Holy holy holy',
  ),
  theme('god', 'glory', 'Glory', 'Glory of God', 'Gods glory', 'Glorious'),
  theme(
    'god',
    'majesty',
    'Majesty',
    'Majesty of God',
    'Greatness of God',
    'How great',
    'Great is the Lord',
  ),
  theme('god', 'awe-and-wonder', 'Awe and Wonder', 'Awe', 'Wonder', 'Amazement'),
  theme(
    'god',
    'sovereignty',
    'Sovereignty',
    'Sovereignty of God',
    'Gods sovereignty',
    'Providence',
    'God in control',
  ),
  theme(
    'god',
    'faithfulness',
    'Faithfulness',
    'Faithfulness of God',
    'Gods faithfulness',
    'Great is thy faithfulness',
  ),
  theme(
    'god',
    'love-of-god',
    'Love of God',
    'Gods love',
    'Steadfast love',
    'Unfailing love',
    'Fathers love',
    'Reckless love',
    'Agape',
    'Divine love',
  ),
  theme(
    'god',
    'mercy',
    'Mercy',
    'Merciful',
    'Mercy of God',
    'Compassion of God',
    'Loving kindness',
  ),
  theme(
    'god',
    'grace',
    'Grace',
    'Amazing grace',
    'Saving grace',
    'Unmerited favour',
    'Unmerited favor',
    'Gracious',
  ),
  theme('god', 'goodness', 'Goodness', 'Goodness of God', 'God is good', 'Kindness of God'),
  theme(
    'god',
    'justice',
    'Justice',
    'Justice of God',
    'Social justice',
    'Caring for the poor',
    'The poor and needy',
    'Oppression',
  ),
  theme(
    'god',
    'righteousness',
    'Righteousness',
    'Righteous',
    'Righteousness of God',
    'Justification',
  ),
  theme('god', 'creation', 'Creation', 'Creator', 'God the Creator', 'Nature', 'Creation care'),
  theme('god', 'power-of-god', 'Power of God', 'Power', 'Mighty God', 'Almighty', 'Omnipotence'),
  theme(
    'god',
    'presence-of-god',
    'Presence of God',
    'Gods presence',
    'In his presence',
    'Dwelling place',
    'Nearness of God',
    'Draw near',
  ),
  theme('god', 'wisdom', 'Wisdom', 'Wisdom of God', 'Gods wisdom', 'Discernment'),
  theme(
    'god',
    'god-the-father',
    'God the Father',
    'The Father',
    'Fatherhood of God',
    'Abba',
    'Abba Father',
  ),
  theme(
    'god',
    'shepherd',
    'The Good Shepherd',
    'Good Shepherd',
    'Shepherd',
    'The Lord is my shepherd',
    'Psalm 23',
  ),
  theme(
    'god',
    'refuge',
    'Refuge',
    'Shelter',
    'Fortress',
    'Strong tower',
    'Hiding place',
    'Rock of ages',
    'Protection',
  ),
  theme(
    'god',
    'provision',
    'Provision',
    'Provider',
    'Jehovah Jireh',
    'Jireh',
    'God provides',
    'Daily bread',
  ),
  theme(
    'god',
    'guidance',
    'Guidance',
    'Guide',
    'Lead me',
    'Direction',
    'Gods leading',
    'Vision',
    'Be thou my vision',
  ),
  theme('god', 'promises-of-god', 'Promises of God', 'Gods promises', 'Promise', 'Promises'),
  theme('god', 'covenant', 'Covenant', 'New covenant', 'Old covenant', 'Covenant faithfulness'),
  theme('god', 'scripture', 'Word of God', 'Scripture', 'The Bible', 'Bible', 'Gods word'),
  theme('god', 'peace', 'Peace', 'Peace of God', 'Prince of Peace', 'Shalom'),
  theme(
    'god',
    'light',
    'Light',
    'Light in darkness',
    'Light of the world',
    'Darkness to light',
    'Light shines',
  ),

  // ---- Christ and his work
  theme('christ', 'name-of-jesus', 'Name of Jesus', 'Jesus name', 'Name above all names', 'Jesus'),
  theme('christ', 'lamb-of-god', 'Lamb of God', 'The Lamb', 'Worthy is the Lamb', 'Passover lamb'),
  theme(
    'christ',
    'atonement',
    'Atonement',
    'Substitution',
    'Ransom',
    'Sacrifice of Christ',
    'Propitiation',
    'Paid in full',
  ),
  theme('christ', 'redemption', 'Redemption', 'Redeemer', 'Redeemed', 'Bought with a price'),
  theme(
    'christ',
    'victory',
    'Victory',
    'Triumph',
    'Overcoming',
    'Death defeated',
    'Victory in Jesus',
    'Conquering',
  ),
  theme(
    'christ',
    'servant-king',
    'Servant King',
    'Suffering servant',
    'Humility of Christ',
    'Christ the servant',
  ),
  theme(
    'christ',
    'healing',
    'Healing',
    'Healer',
    'Jesus heals',
    'Divine healing',
    'Restoration of the body',
  ),
  theme(
    'christ',
    'kingdom-of-god',
    'Kingdom of God',
    'Kingdom',
    'Kingdom of heaven',
    'Your kingdom come',
    'Thy kingdom come',
  ),

  // ---- the Holy Spirit
  theme(
    'spirit',
    'holy-spirit',
    'Holy Spirit',
    'The Spirit',
    'Spirit of God',
    'Holy Ghost',
    'Comforter',
    'Advocate',
    'Spirit-filled',
  ),
  theme('spirit', 'renewal', 'Renewal', 'Revival', 'Restoration', 'Refreshing', 'Awakening'),
  theme(
    'spirit',
    'spiritual-gifts',
    'Spiritual Gifts',
    'Gifts of the Spirit',
    'Fruit of the Spirit',
  ),

  // ---- salvation
  theme(
    'salvation',
    'salvation',
    'Salvation',
    'Saved',
    'Savior',
    'Saviour',
    'Rescue',
    'Deliverance',
    'Lost and found',
  ),
  theme('salvation', 'the-gospel', 'The Gospel', 'Gospel', 'Good news', 'Gospel of grace'),
  theme(
    'salvation',
    'forgiveness',
    'Forgiveness',
    'Forgiven',
    'Pardon',
    'Cleansing',
    'Washed clean',
    'Forgiving others',
  ),
  theme(
    'salvation',
    'repentance',
    'Repentance',
    'Confession',
    'Confession of sin',
    'Turning back',
    'Contrition',
    'Return to God',
  ),
  theme('salvation', 'sin', 'Sin', 'Sin and brokenness', 'Brokenness', 'The Fall', 'Sinfulness'),
  theme(
    'salvation',
    'new-creation',
    'New Creation',
    'New life',
    'Born again',
    'New birth',
    'Regeneration',
    'Made new',
    'Transformation',
  ),
  theme(
    'salvation',
    'freedom',
    'Freedom',
    'Liberty',
    'Set free',
    'Chains broken',
    'Freedom in Christ',
  ),
  theme(
    'salvation',
    'adoption',
    'Adoption',
    'Children of God',
    'Sons and daughters',
    'Adopted',
    'Family of God',
  ),
  theme(
    'salvation',
    'identity-in-christ',
    'Identity in Christ',
    'Identity',
    'In Christ',
    'Chosen',
    'Beloved',
    'Who I am',
  ),
  theme(
    'salvation',
    'eternal-life',
    'Eternal Life',
    'Heaven',
    'Eternity',
    'Life everlasting',
    'New heaven and new earth',
    'New Jerusalem',
  ),
  theme('salvation', 'judgment', 'Judgment', 'Judgement', 'Last judgment', 'Day of judgment'),

  // ---- the Christian life
  theme('life', 'faith', 'Faith', 'Belief', 'Believing', 'Faith in God'),
  theme('life', 'trust', 'Trust', 'Trust in God', 'Trusting God', 'Reliance on God'),
  theme('life', 'hope', 'Hope', 'Living hope', 'Hope in God', 'Anchor of hope', 'Hoping'),
  theme(
    'life',
    'love-for-others',
    'Love for Others',
    'Love one another',
    'Love your neighbor',
    'Love your neighbour',
    'Loving others',
    'Love',
  ),
  theme('life', 'joy', 'Joy', 'Rejoicing', 'Rejoice', 'Gladness', 'Delight'),
  theme('life', 'waiting-on-god', 'Waiting on God', 'Waiting', 'Patience', 'Wait on the Lord'),
  theme(
    'life',
    'perseverance',
    'Perseverance',
    'Endurance',
    'Steadfastness',
    'Running the race',
    'Persevering',
  ),
  theme('life', 'obedience', 'Obedience', 'Obey', 'Doing Gods will', 'Following God'),
  theme(
    'life',
    'discipleship',
    'Discipleship',
    'Following Jesus',
    'Follow me',
    'Disciples',
    'Cost of discipleship',
    'Take up your cross',
  ),
  theme(
    'life',
    'surrender',
    'Surrender',
    'Yielding',
    'All to Jesus',
    'Laying down',
    'Living sacrifice',
  ),
  theme('life', 'prayer', 'Prayer', 'Praying', 'Intercession', 'The Lords Prayer'),
  theme('life', 'worship', 'Worship', 'Adoration', 'Worship of God', 'Exaltation'),
  theme(
    'life',
    'praise',
    'Praise',
    'Praising',
    'Blessing God',
    'Bless the Lord',
    'Hallelujah',
    'Alleluia',
  ),
  theme('life', 'humility', 'Humility', 'Humble', 'Meekness', 'Pride'),
  theme(
    'life',
    'sanctification',
    'Holy Living',
    'Sanctification',
    'Being made holy',
    'Spiritual growth',
    'Growth in grace',
  ),
  theme('life', 'service', 'Service', 'Serving', 'Servanthood', 'Serving others', 'Servant'),
  theme('life', 'generosity', 'Generosity', 'Giving', 'Stewardship', 'Tithing', 'Offering'),
  theme(
    'life',
    'suffering',
    'Suffering',
    'Trials',
    'Hardship',
    'Affliction',
    'Persecution',
    'Pain',
  ),
  theme(
    'life',
    'lament',
    'Lament',
    'Lamentation',
    'Grief',
    'Sorrow',
    'Mourning',
    'Crying out',
    'How long',
  ),
  theme('life', 'comfort', 'Comfort', 'Consolation', 'Comforted', 'Comfort in sorrow'),
  theme('life', 'rest', 'Rest', 'Rest in God', 'Sabbath', 'Weary', 'Come to me'),
  theme(
    'life',
    'fear-and-anxiety',
    'Fear and Anxiety',
    'Fear',
    'Anxiety',
    'Worry',
    'Fear not',
    'Do not be afraid',
  ),
  theme('life', 'courage', 'Courage', 'Boldness', 'Be strong and courageous', 'Bravery'),
  theme(
    'life',
    'strength',
    'Strength',
    'Strength in weakness',
    'Weakness',
    'Gods strength',
    'Strength of God',
  ),
  theme(
    'life',
    'spiritual-warfare',
    'Spiritual Warfare',
    'Armor of God',
    'Armour of God',
    'Spiritual battle',
    'Battle',
  ),
  theme('life', 'temptation', 'Temptation', 'Tempted', 'Testing'),
  theme('life', 'doubt', 'Doubt', 'Doubting', 'Questions', 'Unbelief'),
  theme(
    'life',
    'longing-for-god',
    'Longing for God',
    'Seeking God',
    'Thirst for God',
    'Desire for God',
    'Hunger for God',
  ),
  theme('life', 'calling', 'Calling', 'Vocation', 'Purpose', 'Called'),
  theme('life', 'contentment', 'Contentment', 'Satisfaction', 'Satisfied in God', 'Enough'),
  theme('life', 'reconciliation', 'Reconciliation', 'Reconciled', 'Peace with God', 'Peacemaking'),
  theme('life', 'dedication', 'Dedication', 'Commitment', 'Consecration'),

  // ---- the church and its mission
  theme(
    'church',
    'the-church',
    'The Church',
    'Church',
    'Body of Christ',
    'Bride of Christ',
    'People of God',
  ),
  theme('church', 'unity', 'Unity', 'One body', 'Oneness', 'Unity of the church'),
  theme('church', 'fellowship', 'Fellowship', 'Community', 'Koinonia', 'Belonging'),
  theme(
    'church',
    'evangelism',
    'Evangelism',
    'Sharing the gospel',
    'Witness',
    'Witnessing',
    'Sharing faith',
  ),
  theme(
    'church',
    'missions',
    'Missions',
    'Mission',
    'Great Commission',
    'To the nations',
    'The nations',
    'Go into all the world',
    'Sending',
  ),
  theme(
    'church',
    'call-to-worship',
    'Call to Worship',
    'Gathering',
    'Come let us worship',
    'Opening',
  ),
  theme(
    'church',
    'benediction',
    'Benediction',
    'Blessing',
    'Sending out',
    'Go in peace',
    'The Lord bless you',
  ),

  // ---- sacraments and ordinances
  theme(
    'sacraments',
    'baptism',
    'Baptism',
    'Baptised',
    'Baptized',
    'Water baptism',
    'Buried with Christ',
  ),
  theme(
    'sacraments',
    'communion',
    'Communion',
    'Lords Supper',
    'The Lords Supper',
    'Eucharist',
    'Holy Communion',
    'The table',
    'Bread and wine',
    'Breaking of bread',
  ),

  // ---- occasions
  theme('occasions', 'marriage', 'Marriage', 'Wedding', 'Husband and wife'),
  theme(
    'occasions',
    'family',
    'Family',
    'Parents',
    'Children',
    'Mothers Day',
    'Fathers Day',
    'Home',
  ),
  theme('occasions', 'funeral', 'Funeral', 'Memorial', 'Burial', 'Remembering the dead'),
]

/** A sermon's themes: at most this many. */
export const SERMON_THEME_LIMIT = 6

/**
 * A label as compared: no accents, lower case, no apostrophes ("God's" is
 * "gods"), "&" is "and", punctuation is space, and a leading "the" or "a"
 * goes ("The Cross" is "cross").
 */
export function normaliseThemeLabel(label: string): string {
  return label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['’‘`]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/^(the|a|an) /, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** "lights" is "light", "promises" stays a plural it knows: a crude singular for a second try. */
const singular = (words: string) =>
  words
    .split(' ')
    .map((w) => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w))
    .join(' ')

const bySlug = new Map(THEMES.map((t) => [t.slug, t]))
const byWords = new Map<string, string>()
const bySingular = new Map<string, string>()
for (const entry of THEMES)
  for (const name of [entry.label, entry.slug.replaceAll('-', ' '), ...entry.synonyms]) {
    const words = normaliseThemeLabel(name)
    // The first theme to claim a word keeps it (labels come before synonyms within a theme).
    if (!byWords.has(words)) byWords.set(words, entry.slug)
    if (!bySingular.has(singular(words))) bySingular.set(singular(words), entry.slug)
  }

/** Every slug in the vocabulary. */
export const THEME_SLUGS: readonly string[] = THEMES.map((t) => t.slug)

/** The labels as a model is given them: "Advent, Incarnation, …". */
export const THEME_LABELS: readonly string[] = THEMES.map((t) => t.label)

/** Is this a slug of the vocabulary? */
export const isThemeSlug = (slug: string): boolean => bySlug.has(slug)

/**
 * The vocabulary theme a label means, or null: "Easter" → `resurrection`,
 * "God's love" → `love-of-god`, "the cross" → `the-cross`. A slug is its
 * own theme.
 */
export function themeSlug(label: string): string | null {
  const raw = label.trim()
  if (bySlug.has(raw)) return raw
  const words = normaliseThemeLabel(raw)
  if (!words) return null
  return byWords.get(words) ?? bySingular.get(singular(words)) ?? null
}

/** A slug's label ("resurrection" → "Resurrection"), or null for one not in the vocabulary. */
export function themeLabel(slug: string): string | null {
  return bySlug.get(slug)?.label ?? null
}

/**
 * A sermon's themes read into the vocabulary: each label mapped
 * (`themeSlug`), ones that don't map dropped, nothing twice, at most
 * `max` — the labels people read and the slugs that are counted.
 */
export function vocabularyThemes(
  labels: readonly string[],
  max = SERMON_THEME_LIMIT,
): { themes: string[]; themeSlugs: string[] } {
  const themeSlugs: string[] = []
  for (const label of labels) {
    const slug = themeSlug(label)
    if (!slug || themeSlugs.includes(slug)) continue
    themeSlugs.push(slug)
    if (themeSlugs.length >= max) break
  }
  return { themes: themeSlugs.map((slug) => themeLabel(slug) ?? slug), themeSlugs }
}

/**
 * Themes suggested for a library song: the church's own spelling where it
 * already uses the theme, the vocabulary's label where one maps, anything
 * else as it came (a church keeps its own themes; only the vocabulary's
 * count in the platform's statistics).
 */
export function songThemesInVocabulary(
  suggested: readonly string[],
  existing: readonly string[],
): string[] {
  const own = new Map(existing.map((t) => [normaliseThemeLabel(t), t]))
  return suggested.map((t) => {
    const mine = own.get(normaliseThemeLabel(t))
    if (mine) return mine
    const slug = themeSlug(t)
    return slug ? (themeLabel(slug) ?? t) : t
  })
}

// ---- what churches sing for which themes -------------------------------------------------------

/**
 * A theme's count is shown only from this many churches (as catalog usage:
 * below it, a church could be recognised by what it sings).
 */
export const THEME_MIN_CHURCHES = 3

/** How many churches sang a recording on plans whose sermon had this theme. */
export interface ThemeSignal {
  /** A vocabulary slug. */
  theme: string
  churches: number
}

/** A signal as shown: its label, from three churches up. */
export interface ShownThemeSignal extends ThemeSignal {
  label: string
}

const shown = (signal: ThemeSignal): ShownThemeSignal | null => {
  const label = themeLabel(signal.theme)
  return label && signal.churches >= THEME_MIN_CHURCHES ? { ...signal, label } : null
}

/**
 * The theme most churches sang a recording for, among `among` (a sermon's
 * slugs; all when omitted), from three churches up; null when none can be said.
 */
export function strongestTheme(
  signals: readonly ThemeSignal[],
  among?: readonly string[],
): ShownThemeSignal | null {
  let best: ShownThemeSignal | null = null
  for (const signal of signals) {
    if (among && !among.includes(signal.theme)) continue
    const s = shown(signal)
    if (!s) continue
    if (
      !best ||
      s.churches > best.churches ||
      (s.churches === best.churches &&
        among !== undefined &&
        among.indexOf(s.theme) < among.indexOf(best.theme))
    )
      best = s
  }
  return best
}

const churchWord = (n: number) => (n === 1 ? 'church' : 'churches')

/** "Chosen by 42 churches for Resurrection", or null below three churches. */
export function chosenByLine(signal: ThemeSignal | null | undefined): string | null {
  if (!signal) return null
  const s = shown(signal)
  return s ? `Chosen by ${s.churches} ${churchWord(s.churches)} for ${s.label}` : null
}

/** A song page's "Churches sing it for": the top themes, three churches up, most first. */
export function topThemes(signals: readonly ThemeSignal[], max = 3): ShownThemeSignal[] {
  return signals
    .flatMap((signal) => {
      const s = shown(signal)
      return s ? [s] : []
    })
    .sort((a, b) => b.churches - a.churches || a.label.localeCompare(b.label))
    .slice(0, max)
}

/**
 * How often a song was taken when suggested for a theme, smoothed so a
 * song never suggested sits in the middle (½) and one suggested ten times
 * and never added sinks (1⁄12).
 */
export function acceptanceRate(suggested: number, added: number): number {
  const s = Math.max(0, suggested)
  const a = Math.min(Math.max(0, added), s)
  return (a + 1) / (s + 2)
}

/** A recording's statistics for one theme. */
export interface ThemeStatRow {
  catalogSongId: string
  theme: string
  churches: number
  suggestedCount: number
  addedCount: number
}

/** A recording other churches chose for some of a sermon's themes. */
export interface ThemeCandidate {
  catalogSongId: string
  /** The sermon's themes it was sung for (three churches up), most churches first. */
  themes: ShownThemeSignal[]
  /** The most churches for any one of them. */
  churches: number
  acceptance: number
}

/** At most this many go to the model as "songs other churches chose". */
export const THEME_CANDIDATE_LIMIT = 15

/**
 * The recordings most churches chose for a sermon's themes: each recording
 * once, from three churches up, ranked by churches (its best theme), then
 * how often it was taken when suggested (`acceptanceRate`, over its themes),
 * then the number of the sermon's themes it fits.
 */
export function rankThemeCandidates(
  rows: readonly ThemeStatRow[],
  themes: readonly string[],
  limit = THEME_CANDIDATE_LIMIT,
): ThemeCandidate[] {
  const bySong = new Map<
    string,
    { signals: ShownThemeSignal[]; suggested: number; added: number }
  >()
  for (const row of rows) {
    if (!themes.includes(row.theme)) continue
    const s = shown(row)
    if (!s) continue
    const entry = bySong.get(row.catalogSongId) ?? { signals: [], suggested: 0, added: 0 }
    bySong.set(row.catalogSongId, entry)
    entry.signals.push({ theme: s.theme, churches: s.churches, label: s.label })
    entry.suggested += row.suggestedCount
    entry.added += row.addedCount
  }
  return [...bySong]
    .map(([catalogSongId, entry]) => {
      const signals = entry.signals.sort(
        (a, b) => b.churches - a.churches || themes.indexOf(a.theme) - themes.indexOf(b.theme),
      )
      return {
        catalogSongId,
        themes: signals,
        churches: signals[0]?.churches ?? 0,
        acceptance: acceptanceRate(entry.suggested, entry.added),
      }
    })
    .sort(
      (a, b) =>
        b.churches - a.churches ||
        b.acceptance - a.acceptance ||
        b.themes.length - a.themes.length ||
        a.catalogSongId.localeCompare(b.catalogSongId),
    )
    .slice(0, limit)
}
